import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    HelpCircle, FileText, Eye, Euro, PenTool, User, 
    ChevronDown, ChevronRight, Search, MessageSquare,
    Plus, Clock, CheckCircle, AlertCircle, XCircle,
    Send, ArrowLeft, Loader2, LogIn
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';

const iconMap = {
    FileText: FileText,
    Eye: Eye,
    Euro: Euro,
    PenTool: PenTool,
    User: User,
    HelpCircle: HelpCircle
};

const statusConfig = {
    open: { label: 'Ouvert', color: 'bg-blue-500', icon: AlertCircle },
    in_progress: { label: 'En cours', color: 'bg-yellow-500', icon: Clock },
    waiting_response: { label: 'Réponse reçue', color: 'bg-purple-500', icon: MessageSquare },
    resolved: { label: 'Résolu', color: 'bg-green-500', icon: CheckCircle },
    closed: { label: 'Fermé', color: 'bg-gray-500', icon: XCircle }
};

const priorityConfig = {
    low: { label: 'Basse', color: 'text-muted-foreground' },
    medium: { label: 'Moyenne', color: 'text-yellow-400' },
    high: { label: 'Haute', color: 'text-orange-400' },
    urgent: { label: 'Urgente', color: 'text-red-400' }
};

const categoryConfig = {
    general: 'Général',
    mandate: 'Mandat',
    visit: 'Visite',
    offer: 'Offre',
    signature: 'Signature',
    billing: 'Facturation',
    technical: 'Technique',
    other: 'Autre'
};

export default function SupportPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('faq');
    const [faqData, setFaqData] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [expandedQuestion, setExpandedQuestion] = useState(null);
    const [showNewTicket, setShowNewTicket] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [newTicket, setNewTicket] = useState({
        subject: '',
        message: '',
        category: 'general',
        priority: 'medium'
    });
    const [replyMessage, setReplyMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load FAQ (public)
            const faqRes = await api.get('/support/faq');
            setFaqData(faqRes.data.categories || []);
            
            // Try to load tickets (requires auth)
            try {
                const ticketsRes = await api.get('/support/tickets');
                setTickets(ticketsRes.data.tickets || []);
                setIsAuthenticated(true);
            } catch (err) {
                // User not authenticated - that's OK for FAQ
                setIsAuthenticated(false);
                setTickets([]);
            }
        } catch (err) {
            console.error('Error loading support data:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredFaq = faqData.map(category => ({
        ...category,
        questions: category.questions.filter(q => 
            q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
    })).filter(category => category.questions.length > 0);

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        if (!newTicket.subject || !newTicket.message) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }

        setSubmitting(true);
        try {
            const response = await api.post('/support/tickets', newTicket);
            toast.success('Ticket créé avec succès');
            setTickets([response.data.ticket, ...tickets]);
            setNewTicket({ subject: '', message: '', category: 'general', priority: 'medium' });
            setShowNewTicket(false);
        } catch (err) {
            console.error('Error creating ticket:', err);
            toast.error(err.response?.data?.detail || 'Erreur lors de la création du ticket');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReplyTicket = async () => {
        if (!replyMessage.trim()) return;

        setSubmitting(true);
        try {
            await api.post(`/support/tickets/${selectedTicket.id}/reply`, { message: replyMessage });
            toast.success('Réponse envoyée');
            setReplyMessage('');
            // Reload ticket
            const response = await api.get(`/support/tickets/${selectedTicket.id}`);
            setSelectedTicket(response.data);
            // Update tickets list
            setTickets(tickets.map(t => t.id === selectedTicket.id ? response.data : t));
        } catch (err) {
            toast.error('Erreur lors de l\'envoi de la réponse');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseTicket = async () => {
        if (!window.confirm('Voulez-vous vraiment fermer ce ticket ?')) return;

        try {
            await api.post(`/support/tickets/${selectedTicket.id}/close`);
            toast.success('Ticket fermé');
            setSelectedTicket({ ...selectedTicket, status: 'closed' });
            setTickets(tickets.map(t => t.id === selectedTicket.id ? { ...t, status: 'closed' } : t));
        } catch (err) {
            toast.error('Erreur lors de la fermeture du ticket');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#FF914D] animate-spin" />
            </div>
        );
    }

    // Ticket detail view
    if (selectedTicket) {
        const StatusIcon = statusConfig[selectedTicket.status]?.icon || AlertCircle;
        return (
            <div className="min-h-screen bg-[#0a0a0f] p-6">
                <div className="max-w-4xl mx-auto">
                    <Button
                        onClick={() => setSelectedTicket(null)}
                        variant="ghost"
                        className="text-muted-foreground hover:text-white mb-6"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour aux tickets
                    </Button>

                    <Card className="bg-slate-900 border-border">
                        <CardHeader>
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">{selectedTicket.ticket_number}</p>
                                    <CardTitle className="text-foreground">{selectedTicket.subject}</CardTitle>
                                    <div className="flex items-center gap-3 mt-2">
                                        <Badge className={`${statusConfig[selectedTicket.status]?.color} text-white`}>
                                            <StatusIcon className="w-3 h-3 mr-1" />
                                            {statusConfig[selectedTicket.status]?.label}
                                        </Badge>
                                        <span className="text-sm text-muted-foreground">
                                            {categoryConfig[selectedTicket.category]}
                                        </span>
                                    </div>
                                </div>
                                {selectedTicket.status !== 'closed' && (
                                    <Button
                                        onClick={handleCloseTicket}
                                        variant="outline"
                                        size="sm"
                                        className="border-white/20 text-muted-foreground"
                                    >
                                        Fermer le ticket
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {/* Messages */}
                            <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto">
                                {selectedTicket.messages?.map((msg, idx) => (
                                    <div
                                        key={msg.id || idx}
                                        className={`p-4 rounded-xl ${
                                            msg.sender_type === 'user' 
                                                ? 'bg-card ml-0 mr-8' 
                                                : 'bg-[#FF914D]/10 ml-8 mr-0'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium text-white text-sm">
                                                {msg.sender_name}
                                                {msg.sender_type === 'agent' && (
                                                    <Badge className="ml-2 bg-[#FF914D]/20 text-[#FF914D] text-xs">
                                                        Support
                                                    </Badge>
                                                )}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {new Date(msg.created_at).toLocaleDateString('fr-FR', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </div>
                                        <p className="text-muted-foreground text-sm whitespace-pre-wrap">{msg.message}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Reply form */}
                            {selectedTicket.status !== 'closed' && (
                                <div className="flex gap-3">
                                    <Textarea
                                        value={replyMessage}
                                        onChange={(e) => setReplyMessage(e.target.value)}
                                        placeholder="Votre réponse..."
                                        className="flex-1 bg-card border-border text-white resize-none"
                                        rows={3}
                                    />
                                    <Button
                                        onClick={handleReplyTicket}
                                        disabled={submitting || !replyMessage.trim()}
                                        className="bg-[#FF914D] hover:bg-[#FF914D]/80 text-white self-end"
                                    >
                                        {submitting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0f] p-6">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Centre d'aide</h1>
                    <p className="text-muted-foreground">Trouvez des réponses ou contactez notre support</p>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                    <Button
                        onClick={() => setActiveTab('faq')}
                        className={`${activeTab === 'faq' 
                            ? 'bg-[#FF914D] text-white' 
                            : 'bg-card text-muted-foreground hover:text-white'}`}
                    >
                        <HelpCircle className="w-4 h-4 mr-2" />
                        FAQ
                    </Button>
                    <Button
                        onClick={() => setActiveTab('tickets')}
                        className={`${activeTab === 'tickets' 
                            ? 'bg-[#FF914D] text-white' 
                            : 'bg-card text-muted-foreground hover:text-white'}`}
                    >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Mes tickets
                        {tickets.filter(t => t.status !== 'closed').length > 0 && (
                            <Badge className="ml-2 bg-white/20">{tickets.filter(t => t.status !== 'closed').length}</Badge>
                        )}
                    </Button>
                </div>

                {/* FAQ Tab */}
                {activeTab === 'faq' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        {/* Search */}
                        <div className="relative mb-6">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Rechercher une question..."
                                className="pl-10 bg-card border-border text-white"
                            />
                        </div>

                        {/* FAQ Categories */}
                        <div className="space-y-4">
                            {filteredFaq.map((category) => {
                                const IconComponent = iconMap[category.icon] || HelpCircle;
                                const isExpanded = expandedCategory === category.id;

                                return (
                                    <Card key={category.id} className="bg-slate-900 border-border">
                                        <CardHeader
                                            className="cursor-pointer hover:bg-card transition-colors"
                                            onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 rounded-lg bg-[#FF914D]/20">
                                                        <IconComponent className="w-5 h-5 text-[#FF914D]" />
                                                    </div>
                                                    <div>
                                                        <CardTitle className="text-white text-lg">{category.name}</CardTitle>
                                                        <p className="text-sm text-muted-foreground">{category.questions.length} questions</p>
                                                    </div>
                                                </div>
                                                <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                            </div>
                                        </CardHeader>

                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <CardContent className="pt-0">
                                                        <div className="space-y-2">
                                                            {category.questions.map((item, idx) => {
                                                                const qKey = `${category.id}-${idx}`;
                                                                const isQExpanded = expandedQuestion === qKey;

                                                                return (
                                                                    <div key={idx} className="border-t border-border pt-3">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setExpandedQuestion(isQExpanded ? null : qKey);
                                                                            }}
                                                                            className="w-full flex items-start justify-between text-left"
                                                                        >
                                                                            <span className="font-medium text-white/90">{item.q}</span>
                                                                            <ChevronRight className={`w-4 h-4 text-muted-foreground mt-1 flex-shrink-0 transition-transform ${isQExpanded ? 'rotate-90' : ''}`} />
                                                                        </button>
                                                                        <AnimatePresence>
                                                                            {isQExpanded && (
                                                                                <motion.p
                                                                                    initial={{ height: 0, opacity: 0 }}
                                                                                    animate={{ height: 'auto', opacity: 1 }}
                                                                                    exit={{ height: 0, opacity: 0 }}
                                                                                    className="text-muted-foreground text-sm mt-2 pl-4 border-l-2 border-[#FF914D]/30"
                                                                                >
                                                                                    {item.a}
                                                                                </motion.p>
                                                                            )}
                                                                        </AnimatePresence>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </CardContent>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Contact CTA */}
                        <Card className="mt-8 bg-gradient-to-r from-[#681793]/20 to-[#FF914D]/20 border-border">
                            <CardContent className="p-6 text-center">
                                <h3 className="text-xl font-semibold text-white mb-2">
                                    Vous n'avez pas trouvé votre réponse ?
                                </h3>
                                <p className="text-muted-foreground mb-4">
                                    Notre équipe support est là pour vous aider
                                </p>
                                <Button
                                    onClick={() => { setActiveTab('tickets'); setShowNewTicket(true); }}
                                    className="bg-[#FF914D] hover:bg-[#FF914D]/80 text-white"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Créer un ticket
                                </Button>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Tickets Tab */}
                {activeTab === 'tickets' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        {/* Not authenticated - Show login prompt */}
                        {!isAuthenticated ? (
                            <Card className="bg-slate-900 border-border">
                                <CardContent className="p-8 text-center">
                                    <LogIn className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-white mb-2">
                                        Connectez-vous pour accéder à vos tickets
                                    </h3>
                                    <p className="text-muted-foreground mb-4">
                                        Créez un compte ou connectez-vous pour soumettre et suivre vos demandes de support.
                                    </p>
                                    <Button
                                        onClick={() => navigate('/login')}
                                        className="bg-[#FF914D] hover:bg-[#FF914D]/80 text-white"
                                    >
                                        <LogIn className="w-4 h-4 mr-2" />
                                        Se connecter
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                        <>
                        {/* New Ticket Form */}
                        {showNewTicket ? (
                            <Card className="bg-slate-900 border-border mb-6">
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-foreground">Nouveau ticket</CardTitle>
                                        <Button
                                            onClick={() => setShowNewTicket(false)}
                                            variant="ghost"
                                            size="sm"
                                            className="text-muted-foreground"
                                        >
                                            Annuler
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleCreateTicket} className="space-y-4">
                                        <div>
                                            <label className="text-sm text-muted-foreground mb-1 block">Sujet</label>
                                            <Input
                                                value={newTicket.subject}
                                                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                                placeholder="Décrivez brièvement votre problème"
                                                className="bg-card border-border text-white"
                                                required
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm text-muted-foreground mb-2 block">Catégorie</label>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {Object.entries(categoryConfig).map(([key, label]) => (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            onClick={() => setNewTicket({ ...newTicket, category: key })}
                                                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                                                newTicket.category === key
                                                                    ? 'bg-[#FF914D] text-white'
                                                                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                                                            }`}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground mb-2 block">Priorité</label>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {Object.entries(priorityConfig).map(([key, { label, color }]) => (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            onClick={() => setNewTicket({ ...newTicket, priority: key })}
                                                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                                                newTicket.priority === key
                                                                    ? 'bg-[#FF914D] text-white'
                                                                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                                                            }`}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-sm text-muted-foreground mb-1 block">Message</label>
                                            <Textarea
                                                value={newTicket.message}
                                                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                                                placeholder="Décrivez votre problème en détail..."
                                                className="bg-card border-border text-white min-h-[150px]"
                                                required
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            disabled={submitting}
                                            className="w-full bg-[#FF914D] hover:bg-[#FF914D]/80 text-white"
                                        >
                                            {submitting ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Send className="w-4 h-4 mr-2" />
                                            )}
                                            Envoyer le ticket
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        ) : (
                            <Button
                                onClick={() => setShowNewTicket(true)}
                                className="mb-6 bg-[#FF914D] hover:bg-[#FF914D]/80 text-white"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Nouveau ticket
                            </Button>
                        )}

                        {/* Tickets List */}
                        {tickets.length === 0 ? (
                            <Card className="bg-slate-900 border-border">
                                <CardContent className="p-8 text-center">
                                    <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                    <p className="text-muted-foreground">Vous n'avez pas encore de ticket</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {tickets.map((ticket) => {
                                    const StatusIcon = statusConfig[ticket.status]?.icon || AlertCircle;
                                    return (
                                        <Card
                                            key={ticket.id}
                                            className="bg-slate-900 border-border hover:border-white/20 cursor-pointer transition-colors"
                                            onClick={() => setSelectedTicket(ticket)}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Badge className={`${statusConfig[ticket.status]?.color} text-white text-xs`}>
                                                                <StatusIcon className="w-3 h-3 mr-1" />
                                                                {statusConfig[ticket.status]?.label}
                                                            </Badge>
                                                            <span className="text-xs text-muted-foreground">{ticket.ticket_number}</span>
                                                        </div>
                                                        <h4 className="font-medium text-white">{ticket.subject}</h4>
                                                        <p className="text-sm text-muted-foreground mt-1">
                                                            {categoryConfig[ticket.category]} • {ticket.messages?.length || 1} message(s)
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xs text-muted-foreground">
                                                            {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                                                        </p>
                                                        <ChevronRight className="w-4 h-4 text-muted-foreground mt-2 ml-auto" />
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                        </>
                        )}
                    </motion.div>
                )}
            </div>
        </div>
    );
}
